import React, { FunctionComponent, ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import { Modes } from './Modes';

interface SidebarContainerProps {
  children: ReactNode;
  isExpanded: boolean;
  mode: Modes.FULL | Modes.MINIMIZED;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const SidebarContainer: FunctionComponent<
  React.PropsWithChildren<SidebarContainerProps>
> = ({ children, isExpanded, mode, onMouseEnter, onMouseLeave }) => {
  const Component = ComponentMap[mode];

  return (
    <Component
      id='sidebarnav'
      isExpanded={isExpanded}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Component>
  );
};

// Fills the shell rather than the viewport: a top banner shortens the shell,
// and a viewport-sized sidebar would then overhang it by the banner's height.
const containerGridStyle = `
  display: grid;
  grid-template-rows: 1fr auto;
  min-height: 100%;
  z-index: 1201;
`;

const MinimizedContainer = styled('div')<{
  isExpanded: boolean;
}>`
  position: ${props => (props.isExpanded ? 'relative' : 'absolute')};
  top: ${props => (props.isExpanded ? 'auto' : '0')};
  left: ${props => (props.isExpanded ? 'auto' : '0')};
  bottom: ${props => (props.isExpanded ? 'auto' : '0')};
  z-index: ${props => (props.isExpanded ? 'auto' : 2)};
  transition-duration: var(--duration-fast);
  will-change: width box-shadow;
  min-width: ${props =>
    props.isExpanded
      ? 'var(--width-sidebar)'
      : 'var(--width-minimized-sidebar)'};
  box-shadow: ${props =>
    props.isExpanded ? 'var(--box-shadow-sidebar)' : undefined};
  ${containerGridStyle}
`;
const FullContainer = styled('div')`
  min-width: var(--width-sidebar);
  ${containerGridStyle}
`;

const ComponentMap: {
  [key: string]: typeof FullContainer | typeof MinimizedContainer;
} = {
  full: FullContainer,
  minimized: MinimizedContainer,
};

export default SidebarContainer;
